import type {
	FeedMeta,
	FeedPage,
	FetchShortsOptions
} from '~/composables/shorts/useFetchShorts';
import type { ShortsFilters } from '~/core/entities/property/filters';
import type { MaybeRefOrGetter } from 'vue';

import {
	SHORTS_DEFAULT_PER_PAGE,
	useFetchShorts
} from '~/composables/shorts/useFetchShorts';
import { useStaleGuard } from '~/utils';

import { uniqueFeedItemsByCommercialId } from './uniqueFeedItemsByCommercialId';

export interface UseFeedSessionOptions {
	perPage?: number;
	startPage?: MaybeRefOrGetter<number>;
	fetchOptions?: FetchShortsOptions;
	toRequestFilters?: (filters: ShortsFilters) => ShortsFilters;
	initialPage?: FeedPage | null;
}

/**
 * Dono único de uma lista de Shorts. A sessão concentra itens, cursor, meta,
 * dedupe e descarte de respostas obsoletas; as telas só iniciam ou continuam a
 * sessão.
 */
export function useFeedSession(options: UseFeedSessionOptions = {}) {
	const guard = useStaleGuard();
	const initialPage = options.initialPage;
	const defaultPerPage = options.perPage ?? SHORTS_DEFAULT_PER_PAGE;
	const fetcher = useFetchShorts();

	let activeFetchOptions = { ...options.fetchOptions };

	const items = shallowRef<FeedPage['items']>(
		uniqueFeedItemsByCommercialId(initialPage?.items ?? [])
	);
	const filters = shallowRef<ShortsFilters>(initialPage?.cursor.filters ?? {});
	const page = ref(initialPage?.cursor.page ?? 1);
	const perPage = ref(initialPage?.cursor.perPage ?? defaultPerPage);
	const hasMore = ref(initialPage?.cursor.hasMore ?? false);
	const meta = shallowRef<FeedMeta>({ ...initialPage?.meta });

	const isLoading = ref(false);
	const isLoadingMore = ref(false);
	const isLoaded = ref(initialPage !== undefined && initialPage !== null);
	const isStopped = ref(false);

	const cursor = computed(() => ({
		filters: filters.value,
		page: page.value,
		perPage: perPage.value,
		hasMore: hasMore.value
	}));
	const allWatched = computed({
		get: () => meta.value.allWatched ?? false,
		set: value => {
			meta.value = { ...meta.value, allWatched: value };
		}
	});
	const totalAvailable = computed(
		() => meta.value.totalAvailable ?? items.value.length
	);
	const recommendationCountVideos = computed(
		() => meta.value.counts?.totalWithVideos
	);
	const recommendationCountPhotos = computed(
		() => meta.value.counts?.totalWithoutVideos
	);
	const sectionTitle = computed(() => meta.value.sectionTitle);
	const recommendationType = computed(() => meta.value.recommendationType);
	const isEmpty = computed(() => isLoaded.value && items.value.length === 0);

	function applyPage(nextPage: FeedPage, isReset: boolean): void {
		items.value = uniqueFeedItemsByCommercialId(
			isReset ? nextPage.items : [...items.value, ...nextPage.items]
		);
		filters.value = nextPage.cursor.filters;
		page.value = nextPage.cursor.page;
		perPage.value = nextPage.cursor.perPage;
		hasMore.value = nextPage.cursor.hasMore;
		meta.value = isReset
			? { ...nextPage.meta }
			: {
					...meta.value,
					...nextPage.meta,
					counts: nextPage.meta.counts ?? meta.value.counts
				};
		isLoaded.value = true;
	}

	async function load(
		requestedFilters: ShortsFilters = {},
		fetchOptions: FetchShortsOptions = {}
	): Promise<void> {
		if (isStopped.value) return;

		const capturedId = guard.next();
		const targetPage = toValue(options.startPage) ?? 1;
		const requestFilters =
			options.toRequestFilters?.(requestedFilters) ?? requestedFilters;
		activeFetchOptions = { ...options.fetchOptions, ...fetchOptions };
		isLoading.value = true;
		isLoadingMore.value = false;

		try {
			const result = await fetcher.fetchVideos(
				requestFilters,
				targetPage,
				perPage.value,
				activeFetchOptions
			);
			if (guard.isStale(capturedId)) return;

			applyPage(result, true);
		} catch (error) {
			if (guard.isStale(capturedId)) return;
			throw error;
		} finally {
			if (!guard.isStale(capturedId)) isLoading.value = false;
		}
	}

	async function loadMore(): Promise<boolean> {
		if (
			isStopped.value ||
			isLoading.value ||
			isLoadingMore.value ||
			!hasMore.value
		) {
			return false;
		}

		const capturedId = guard.next();
		const previousLength = items.value.length;
		isLoadingMore.value = true;

		try {
			const result = await fetcher.fetchVideos(
				filters.value,
				page.value + 1,
				perPage.value,
				activeFetchOptions
			);
			if (guard.isStale(capturedId)) return false;

			applyPage(result, false);
			return items.value.length > previousLength;
		} finally {
			if (!guard.isStale(capturedId)) isLoadingMore.value = false;
		}
	}

	function seed(feedPage: FeedPage): void {
		guard.next();
		applyPage(feedPage, true);
		isLoading.value = false;
		isLoadingMore.value = false;
	}

	function snapshot(fromIndex = 0): FeedPage {
		const skippedItems = items.value.slice(0, fromIndex);
		const skippedVideos = skippedItems.filter(
			item => item.kind === 'video'
		).length;
		const skippedPhotos = skippedItems.length - skippedVideos;
		const counts = meta.value.counts;

		return {
			items: items.value.slice(fromIndex),
			cursor: { ...cursor.value, filters: { ...filters.value } },
			meta: {
				...meta.value,
				counts: counts
					? {
							totalWithVideos: Math.max(
								0,
								counts.totalWithVideos - skippedVideos
							),
							totalWithoutVideos: Math.max(
								0,
								counts.totalWithoutVideos - skippedPhotos
							)
						}
					: undefined
			}
		};
	}

	function reset(): void {
		guard.next();
		items.value = [];
		filters.value = {};
		page.value = 1;
		perPage.value = defaultPerPage;
		hasMore.value = false;
		meta.value = {};
		isLoading.value = false;
		isLoadingMore.value = false;
		isLoaded.value = false;
		activeFetchOptions = { ...options.fetchOptions };
	}

	function stop(): void {
		isStopped.value = true;
		guard.next();
	}

	function resume(): void {
		isStopped.value = false;
	}

	if (getCurrentScope()) onScopeDispose(stop);

	function remainingAfter(commercialId: string): {
		videos: number | undefined;
		photos: number | undefined;
	} {
		const index = items.value.findIndex(
			item => item.property.commercialId === commercialId
		);
		const counts = meta.value.counts;

		if (index === -1) {
			return {
				videos: counts?.totalWithVideos,
				photos: counts?.totalWithoutVideos
			};
		}

		const watchedVideos = items.value
			.slice(0, index + 1)
			.filter(item => item.kind === 'video').length;
		const watchedPhotos = index + 1 - watchedVideos;

		return {
			videos:
				counts?.totalWithVideos !== undefined
					? Math.max(0, counts.totalWithVideos - watchedVideos)
					: undefined,
			photos:
				counts?.totalWithoutVideos !== undefined
					? Math.max(0, counts.totalWithoutVideos - watchedPhotos)
					: undefined
		};
	}

	return {
		items,
		cursor,
		meta,
		filters,
		page,
		perPage,
		hasMore,
		allWatched,
		totalAvailable,
		recommendationCountVideos,
		recommendationCountPhotos,
		sectionTitle,
		recommendationType,
		isLoading,
		isLoadingMore,
		isLoaded,
		isStopped,
		isEmpty,
		load,
		loadMore,
		seed,
		snapshot,
		reset,
		stop,
		resume,
		remainingAfter
	};
}

export type FeedSession = ReturnType<typeof useFeedSession>;
